// src/pages/Admin/Components/CategoriesPage.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  Plus, Edit, Trash2, Loader2, 
  CheckCircle, XCircle, Search, X, 
  ChevronLeft, ChevronRight 
} from 'lucide-react';
import { getAllCategories, createCategory, updateCategory, deleteCategory } from '../../../services/categoryServices';

const CategoriesPage = () => {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth) || {};
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', icon: '', is_active: true });
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchCategories = async () => {
    if (!userInfo?.access) return;
    setLoading(true);
    try {
      const res = await getAllCategories({ userInfo, dispatch });
      setCategories(res.data);
    } catch (err) {
      setError('خطا در دریافت دسته‌بندی‌ها');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [userInfo]);

  const filteredCategories = categories.filter(cat =>
    cat.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalItems = filteredCategories.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentItems = filteredCategories.slice(startIndex, endIndex);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setFormError('نام دسته‌بندی الزامی است.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      await createCategory(formData, { userInfo, dispatch });
      setFormData({ name: '', icon: '', is_active: true });
      setIsModalOpen(false);
      fetchCategories();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'خطا در ایجاد دسته‌بندی');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (!formData.name.trim()) {
      setFormError('نام دسته‌بندی الزامی است.');
      return;
    }
    setSubmitting(true);
    setFormError('');
    try {
      await updateCategory(editingCategory.id, formData, { userInfo, dispatch });
      setFormData({ name: '', icon: '', is_active: true });
      setEditingCategory(null);
      setIsModalOpen(false);
      fetchCategories();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'خطا در ویرایش دسته‌بندی');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (category) => {
    if (category.products_count > 0) {
      alert('این دسته‌بندی دارای محصول است و قابل حذف نیست.');
      return;
    }
    if (!window.confirm(`آیا از حذف دسته‌بندی "${category.name}" مطمئن هستید؟`)) return;
    try {
      await deleteCategory(category.id, { userInfo, dispatch });
      fetchCategories();
    } catch (err) {
      alert(err.response?.data?.detail || 'خطا در حذف دسته‌بندی');
    }
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, icon: category.icon || '', is_active: category.is_active });
    setFormError('');
    setIsModalOpen(true);
  };

  const openCreateModal = () => {
    setEditingCategory(null);
    setFormData({ name: '', icon: '', is_active: true });
    setFormError('');
    setIsModalOpen(true);
  };

  const SkeletonRow = () => (
    <tr className="animate-pulse border-b border-gray-100">
      <td className="px-6 py-4"><div className="h-5 w-20 bg-gray-200 rounded"></div></td>
      <td className="px-6 py-4"><div className="h-5 w-32 bg-gray-200 rounded"></div></td>
      <td className="px-6 py-4"><div className="h-5 w-16 bg-gray-200 rounded"></div></td>
      <td className="px-6 py-4 text-center"><div className="h-6 w-16 bg-gray-200 rounded mx-auto"></div></td>
      <td className="px-6 py-4 text-center"><div className="h-6 w-20 bg-gray-200 rounded mx-auto"></div></td>
      <td className="px-6 py-4 text-center"><div className="h-8 w-24 bg-gray-200 rounded mx-auto"></div></td>
    </tr>
  );

  if (loading) {
    return (
      <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded-xl"></div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <table className="w-full min-w-[700px]">
              <tbody>
                {[...Array(5)].map((_, i) => <SkeletonRow key={i} />)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">خطا: {error}</p>
          <button onClick={fetchCategories} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen" dir="rtl">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 border-b border-gray-200 pb-6">
        <div>
          <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight mb-2">
            مدیریت دسته‌بندی‌ها
          </h1>
          <p className="text-sm text-gray-500">
            دسته‌بندی‌های محصولات را مدیریت کنید.
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-colors shadow-md shadow-blue-200"
        >
          <Plus className="w-5 h-5" />
          افزودن دسته‌بندی جدید
        </button>
      </div>

      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-200 mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="relative w-full md:w-72">
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Search className="w-5 h-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="جستجو بر اساس نام..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
          />
          {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm('');
                setCurrentPage(1);
              }}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="text-sm text-gray-500">
          <span className="font-bold">{totalItems}</span> دسته‌بندی
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[700px]">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr className="text-right text-sm font-bold text-gray-600 uppercase tracking-wider">
                <th className="px-6 py-4 text-right">شناسه</th>
                <th className="px-6 py-4 text-right">نام</th>
                <th className="px-6 py-4 text-right">آیکون</th>
                <th className="px-6 py-4 text-center">وضعیت</th>
                <th className="px-6 py-4 text-center">تعداد محصول</th>
                <th className="px-6 py-4 text-center bg-gray-50 border-r border-gray-200">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentItems.length > 0 ? (
                currentItems.map((cat) => (
                  <tr key={cat.id} className="hover:bg-blue-50/30 transition-colors">
                    <td className="px-6 py-4 text-sm text-gray-500">#{cat.id}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-800">{cat.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-500">{cat.icon || '—'}</td>
                    <td className="px-6 py-4 text-center">
                      {cat.is_active ? (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200">
                          <CheckCircle className="w-3.5 h-3.5" /> فعال
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                          <XCircle className="w-3.5 h-3.5" /> غیرفعال
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-center text-sm text-gray-500">
                      {cat.products_count || 0}
                    </td>
                    <td className="px-6 py-4 text-center border-r border-gray-200">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openEditModal(cat)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="ویرایش"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(cat)}
                          className={`p-2 rounded-lg transition-colors ${
                            cat.products_count > 0 
                              ? 'text-gray-300 cursor-not-allowed' 
                              : 'text-red-400 hover:bg-red-50 hover:text-red-600'
                          }`}
                          title={cat.products_count > 0 ? 'این دسته‌بندی دارای محصول است' : 'حذف'}
                          disabled={cat.products_count > 0}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="text-center py-12 text-gray-500 font-medium">
                    دسته‌بندی‌ای یافت نشد.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 gap-4">
            <div className="text-sm text-gray-600 font-medium order-2 sm:order-1">
              نمایش <span className="font-bold text-gray-900">{startIndex + 1}</span> تا{' '}
              <span className="font-bold text-gray-900">{endIndex}</span> از{' '}
              <span className="font-bold text-gray-900">{totalItems}</span>
            </div>
            <div className="flex items-center gap-2 order-1 sm:order-2 bg-white p-1 rounded-lg shadow-sm border border-gray-200">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-9 h-9 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  onClick={() => setCurrentPage(p)}
                  className={`w-9 h-9 flex items-center justify-center rounded text-sm font-bold transition-colors ${
                    currentPage === p
                      ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-9 h-9 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {editingCategory ? 'ویرایش دسته‌بندی' : 'افزودن دسته‌بندی جدید'}
              </h2>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingCategory(null);
                  setFormError('');
                }}
                className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {formError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  نام دسته‌بندی <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="مثال: مبلمان"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  آیکون (اختیاری)
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="نام آیکون (مثلاً: FaHome)"
                />
              </div>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  فعال
                </label>
              </div>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setEditingCategory(null);
                  setFormError('');
                }}
                className="px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors"
              >
                انصراف
              </button>
              <button
                onClick={editingCategory ? handleUpdate : handleCreate}
                disabled={submitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {editingCategory ? 'ذخیره تغییرات' : 'افزودن'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoriesPage;